"use client";

import { useEffect, useMemo, useState } from "react";
import PhoneField from "./PhoneField";
import DisclaimerNotice from "./DisclaimerNotice";
import { toA2 } from "@/features/landing/utils/country-name";

export default function DynamicFieldForm({
  requiredFields = [],
  onSubmit,
  user,
  countryCode,
  isBlocked = false,
  blockMessage = "Our payment systems are currently being upgraded. Payments are unavailable until April 4, 2026.",
}) {
  const [formData, setFormData] = useState({});
  const [confirmPhones, setConfirmPhones] = useState({});
  const selectedCountryCode = useMemo(() => {
  const raw = countryCode || user?.countryCode || "US";
  return toA2(raw) || "US";
}, [countryCode, user?.countryCode]);


  const [showDisclaimer, setShowDisclaimer] = useState(false);
  useEffect(() => {
    try {
      const accepted = window.localStorage.getItem("disclaimerAccepted");
      setShowDisclaimer(!accepted);
    } catch {
      setShowDisclaimer(true);
    }
  }, []);

  const handleDismiss = () => {
    try {
      window.localStorage.setItem("disclaimerAccepted", "1");
    } catch {}
    setShowDisclaimer(false);
  };

  const sanitizePhone = (v = "") => v.replace(/[^\d+]/g, "").replace(/^\+?/, "+");

  const handleChange = (path, value, isConfirm = false) => {
    const sanitized =
      path.toLowerCase().includes("phone") || path.toLowerCase().includes("msisdn")
        ? sanitizePhone(value ?? "")
        : value;

    (isConfirm ? setConfirmPhones : setFormData)((prev) => ({ ...prev, [path]: sanitized }));
  };

  const getInputType = (field) => {
    const f = field.toLowerCase();
    if (f.includes("email")) return "email";
    if (f.includes("phone") || f.includes("msisdn")) return "tel";
    if (f.includes("date")) return "date";
    if (f.includes("amount") || f.includes("price")) return "number";
    return "text";
  };

  const formatLabel = (field) =>
    field
      .replace(/\./g, " ")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const fields = useMemo(() => (Array.isArray(requiredFields) ? requiredFields : []), [requiredFields]);

  const isFormValid = fields.every((f) => {
    const isPhone = f.toLowerCase().includes("phone") || f.toLowerCase().includes("msisdn");
    const value = formData[f];
    const confirm = confirmPhones[f];
    return typeof value === "string" && value.trim() && (!isPhone || value === confirm);
  });

  const canSubmit = isFormValid && !isBlocked;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isBlocked) return;

    const submission = { ...formData };
    if (fields.includes("recipient.country") && !submission["recipient.country"] && selectedCountryCode) {
      submission["recipient.country"] = selectedCountryCode;
    }
    onSubmit?.(submission);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {fields.map((field) => {
        const isPhone = field.toLowerCase().includes("phone") || field.toLowerCase().includes("msisdn");
        return (
          <div key={field} className="space-y-2">
            {isPhone ? (
              <PhoneField
                label={formatLabel(field)}
                country={selectedCountryCode}
                onChange={(main, confirm) => {
                  handleChange(field, main);
                  handleChange(field, confirm, true);
                }}
              />
            ) : (
              <>
                <label className="block text-sm font-medium text-textSecondary mb-1">{formatLabel(field)}</label>
                <input
                  type={getInputType(field)}
                  value={formData[field] || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-surfaceMuted border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
              </>
            )}
          </div>
        );
      })}

      {showDisclaimer &&
        fields.some((f) => f.toLowerCase().includes("phone") || f.toLowerCase().includes("msisdn")) && (
          <DisclaimerNotice onDismiss={handleDismiss} />
        )}

      <button
        type="submit"
        disabled={!canSubmit}
        className={`w-full min-h-[44px] px-4 rounded-xl font-semibold transition ${
          canSubmit ? "bg-accent text-white hover:bg-accentHover" : "bg-surfaceElevated text-textMuted cursor-not-allowed"
        }`}
      >
        Continue to Crypto Checkout
      </button>

      {isBlocked && <p className="text-sm text-error mt-1">{blockMessage}</p>}
    </form>
  );
}
